import { forwardRef } from 'react';
import FormControl from '@mui/material/FormControl';
import InputLabel from '@mui/material/InputLabel';
import Select, { type SelectProps } from '@mui/material/Select';
import MenuItem from '@mui/material/MenuItem';
import FormHelperText from '@mui/material/FormHelperText';

export interface AppSelectOption {
  value: string | number;
  label: string;
  disabled?: boolean;
}

export interface AppSelectProps extends Omit<SelectProps, 'variant'> {
  /**
   * Options for the select
   */
  options: AppSelectOption[];
  /**
   * Helper text shown below the select
   */
  helperText?: React.ReactNode;
  /**
   * Error message
   */
  errorText?: string;
  /**
   * Show a placeholder option
   */
  placeholder?: string;
}

/**
 * AppSelect - Dropdown select component
 *
 * Wraps MUI Select with our design system patterns.
 * Use for single-value selection from a list.
 */
export const AppSelect = forwardRef<HTMLDivElement, AppSelectProps>(
  (
    {
      options,
      helperText,
      errorText,
      placeholder,
      label,
      error,
      fullWidth = true,
      size = 'small',
      ...props
    },
    ref
  ) => {
    const hasError = error || !!errorText;

    return (
      <FormControl fullWidth={fullWidth} size={size} error={hasError} ref={ref}>
        {label && <InputLabel>{label}</InputLabel>}
        <Select label={label} {...props}>
          {placeholder && (
            <MenuItem value="" disabled>
              <em>{placeholder}</em>
            </MenuItem>
          )}
          {options.map((option) => (
            <MenuItem
              key={option.value}
              value={option.value}
              disabled={option.disabled}
            >
              {option.label}
            </MenuItem>
          ))}
        </Select>
        {(errorText || helperText) && (
          <FormHelperText>{errorText || helperText}</FormHelperText>
        )}
      </FormControl>
    );
  }
);

AppSelect.displayName = 'AppSelect';

// Re-export MenuItem for custom rendering
export { MenuItem };
